const STORAGE_KEY = 'sc-reading-preferences';
const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const systemMotion = typeof window.matchMedia === 'function'
  ? window.matchMedia(REDUCE_MOTION_QUERY)
  : { matches: false };

let preferences = readPreferences();
const dialogControllers = new WeakMap();

function readPreferences() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return { motion: false, text: false };
    return { motion: stored.motion === true, text: stored.text === true };
  } catch (_error) {
    return { motion: false, text: false };
  }
}

function savePreferences() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (_error) {
    /* Storage may be blocked in private/restricted contexts; in-page state still works. */
  }
}

function applyPreferences() {
  const systemRequiresReducedMotion = systemMotion.matches === true;
  document.documentElement.classList.toggle('reduce-site-motion', systemRequiresReducedMotion || preferences.motion);
  document.documentElement.classList.toggle('larger-site-text', preferences.text);

  document.querySelectorAll('[data-sc-reading-preferences]').forEach((dialog) => {
    const motionInput = dialog.querySelector('[data-sc-motion-input]');
    const textInput = dialog.querySelector('[data-sc-text-input]');
    const systemNote = dialog.querySelector('[data-sc-system-motion-note]');

    if (motionInput) {
      motionInput.checked = systemRequiresReducedMotion || preferences.motion;
      motionInput.disabled = systemRequiresReducedMotion;
      motionInput.setAttribute('aria-disabled', String(systemRequiresReducedMotion));
    }
    if (textInput) textInput.checked = preferences.text;
    if (systemNote) systemNote.hidden = !systemRequiresReducedMotion;
  });
}

function dialogIsOpen(dialog) {
  return dialog.open || dialog.hasAttribute('open');
}

function focusableElements(dialog) {
  return [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
    return !element.disabled && element.getAttribute('aria-hidden') !== 'true';
  });
}

function initializeDialog(dialog) {
  if (dialog.dataset.scReadingPreferencesReady === 'true') return;
  dialog.dataset.scReadingPreferencesReady = 'true';

  const closeButton = dialog.querySelector('[data-sc-reading-preferences-close]');
  const motionInput = dialog.querySelector('[data-sc-motion-input]');
  const textInput = dialog.querySelector('[data-sc-text-input]');
  let returnFocus = null;
  let fallbackModal = false;

  function restoreFocus() {
    if (returnFocus && returnFocus.isConnected && typeof returnFocus.focus === 'function') returnFocus.focus();
    returnFocus = null;
  }

  function closeDialog() {
    if (!dialogIsOpen(dialog)) return;
    if (!fallbackModal && typeof dialog.close === 'function') {
      dialog.close();
      return;
    }
    dialog.removeAttribute('open');
    dialog.removeAttribute('aria-modal');
    fallbackModal = false;
    restoreFocus();
  }

  function openDialog(trigger) {
    returnFocus = trigger;
    applyPreferences();
    if (!dialogIsOpen(dialog)) {
      try {
        if (typeof dialog.showModal !== 'function') throw new Error('Modal dialog is unsupported');
        dialog.showModal();
        fallbackModal = false;
      } catch (_error) {
        dialog.setAttribute('open', '');
        dialog.setAttribute('aria-modal', 'true');
        fallbackModal = true;
      }
    }
    window.requestAnimationFrame(() => closeButton?.focus());
  }

  dialogControllers.set(dialog, { open: openDialog });

  closeButton?.addEventListener('click', closeDialog);
  dialog.addEventListener('close', restoreFocus);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = focusableElements(dialog);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  motionInput?.addEventListener('change', () => {
    if (systemMotion.matches === true) {
      applyPreferences();
      return;
    }
    preferences.motion = motionInput.checked;
    applyPreferences();
    savePreferences();
  });

  textInput?.addEventListener('change', () => {
    preferences.text = textInput.checked;
    applyPreferences();
    savePreferences();
  });
}

function initializeReadingPreferences(root = document) {
  root.querySelectorAll('[data-sc-reading-preferences]').forEach(initializeDialog);
  applyPreferences();
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-sc-reading-preferences-open]');
  if (!trigger) return;

  const targetId = trigger.getAttribute('data-sc-reading-preferences-open') || trigger.getAttribute('aria-controls');
  const dialog = (targetId && document.getElementById(targetId))
    || document.querySelector('[data-sc-reading-preferences]');
  if (!dialog) return;

  initializeDialog(dialog);
  dialogControllers.get(dialog)?.open(trigger);
});

if (typeof systemMotion.addEventListener === 'function') {
  systemMotion.addEventListener('change', applyPreferences);
} else if (typeof systemMotion.addListener === 'function') {
  systemMotion.addListener(applyPreferences);
}

window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  preferences = readPreferences();
  applyPreferences();
});

document.addEventListener('shopify:section:load', (event) => initializeReadingPreferences(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeReadingPreferences(), { once: true });
} else {
  initializeReadingPreferences();
}

